<template>
  <div class="feedback-container">
    <header class="feedback-header pa-4">
      <div>
        <Breadcrumbs headline="Phản hồi" :link="[{ text: 'Phản hồi', href: '../feedback' }]" />
      </div>
    </header>
    <section class="feedback-filter mb-6">
      <v-card class="mx-4 pa-6" elevation="0" outlined>
        <TeacherFeedbackFilter @onFilterChanged="onFilterChanged" />
      </v-card>
    </section>

    <section v-if="firstLoadData || (messageGroups && messageGroups.length)" class="pa-6">
      <v-row>
        <v-col class="pa-0" cols="12" md="4">
          <div
            v-infinite-scroll="loadMore"
            :infinite-scroll-disabled="busy"
            class="message-groups-list px-4"
            :style="{ height: `${windowHeight - 300}px` }"
          >
            <v-row>
              <v-col class="pl-0" v-for="(group, index) in messageGroups" :key="index" cols="12">
                <v-hover v-slot="{ hover }" open-delay="200">
                  <v-card
                    :class="['message-group-card', { 'on-hover': hover, selected: group.selected }]"
                    outlined
                    @click="groupClicked(group)"
                  >
                    <v-card-text>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <div class="message-group-content">
                            <span v-bind="attrs" v-on="on">
                              <div class="d-flex justify-space-between align-center">
                                <div>
                                  <div class="message-preview">{{ group | getTeacherNames }}</div>
                                  <CardStudentName :student="group.student" class="student-name" />
                                </div>
                              </div>
                              <div class="message-preview">
                                {{ group | getLastMessageContent }}
                              </div>
                            </span>
                          </div>
                        </template>
                        <span>{{ group.student.name }} - Lớp {{ group | _get('class.title', 'Không có') }} - {{ group.name }}</span>
                      </v-tooltip>
                    </v-card-text>
                  </v-card>
                </v-hover>
              </v-col>

              <!-- Loading State -->
              <v-col v-if="busy">
                <v-skeleton-loader type="card-avatar, article"></v-skeleton-loader>
              </v-col>
            </v-row>
          </div>
        </v-col>

        <!-- Chat Window -->
        <v-col cols="12" md="8" class="chat-window">
          <template v-if="messageGroup && messageGroup.id">
            <div id="chat" v-if="!messageLoading" class="chat-container">
              <!-- Chat Header -->
              <div class="chat-header d-flex align-center justify-space-between mb-4">
                <h3 class="primary--text">Phụ huynh em {{ messageGroup | _get('student.name') }}</h3>
                <router-link class="view-profile-link" :to="'/student/' + messageGroup.student.id">
                  Xem hồ sơ
                </router-link>
              </div>

              <!-- Messages -->
              <div
                v-chat-scroll="{ always: false, scrollonremoved: true }"
                class="messages-container"
                :style="{ height: `${windowHeight - 450}px` }"
              >
                <div v-for="message in messages" :key="message.id" class="message-wrapper">
                  <!-- System Message -->
                  <div v-if="message.data && message.data.type === 'other'" class="system-message">
                    <span class="text-caption">{{ message.content }}</span>
                    <span class="message-time text-caption">{{ message.time | getDisplayTime }}</span>
                  </div>

                  <!-- User Message -->
                  <div
                    v-else
                    :class="['message-bubble', message.sender.type !== 'student' ? 'sent' : 'received']"
                  >
                    <!-- Image Message -->
                    <div v-if="message.image" class="image-message">
                      <img :src="message.image.url" />
                    </div>

                    <!-- Text Message -->
                    <div v-if="message.content" class="text-message">
                      {{ message.content }}
                    </div>

                    <!-- Timestamp -->
                    <div class="message-time">
                      {{ message.time | getDisplayTime }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Input Area -->
              <div class="chat-input-area">
                <v-row no-gutters align="center">
                  <v-col cols="auto" class="mr-2">
                    <v-file-input
                      :disabled="role.type !== 'manager'"
                      dense
                      accept="image/png, image/jpeg, image/bmp"
                      prepend-icon="mdi-camera"
                      hide-input
                      @change="onChange"
                    ></v-file-input>
                  </v-col>
                  <v-col>
                    <v-text-field
                      v-model="content"
                      placeholder="Nhập phản hồi..."
                      :disabled="sending || role.type !== 'manager'"
                      :loading="sending"
                      outlined
                      dense
                      hide-details
                      append-icon="mdi-send"
                      @click:append="send()"
                      @keyup.enter="send()"
                    />
                  </v-col>
                </v-row>
              </div>
            </div>

            <!-- Loading State -->
            <div v-else>
              <v-skeleton-loader type="article, article, article"></v-skeleton-loader>
            </div>
          </template>

          <!-- Empty State -->
          <div v-else class="empty-state">
            <div>Vui lòng chọn cuộc hội thoại</div>
          </div>
        </v-col>
      </v-row>
    </section>

    <!-- No Feedback State -->
    <div v-else class="no-feedback">
      <h2>Chưa có phản hồi từ phụ huynh</h2>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import Breadcrumbs from '@/components/layout/Breadcrumbs'
import { textHelpers } from '@/helpers/TextHelper.js'
import TeacherFeedbackFilter from '@/modules/feedback/TeacherFeedbackFilter.vue'
import Vue from 'vue'
import VueChatScroll from 'vue-chat-scroll'
import moment from 'moment'
import CardStudentName from '@/components/basic/card/CardStudentName.vue'
import infiniteScroll from 'vue-infinite-scroll'
Vue.use(infiniteScroll)
Vue.use(VueChatScroll)

export default {
  components: {
    Breadcrumbs,
    CardStudentName,
    TeacherFeedbackFilter
  },
  data() {
    return {
      windowHeight: window.innerHeight,
      content: '',
      interval: null,
      sending: false,
      busy: false,
      messageGroupParams: {},
      firstLoadData: false,
      messageLoading: false,
      reloadCurrentRoom: false,
      searchParams: {}
    }
  },
  async created() {
    this.firstLoadData = true

    this.messageGroupParams = {
      type: 'teacher'
    }
    await this.fetchMessageGroups({ ...this.messageGroupParams, _limit: 15 })

    if (this.$route.params.id) {
      const group = this.messageGroups.find(g => g.id === this.$route.params.id)
      if (group && group.id) await this.groupClicked(group)
      else this.$router.push(`/teacher-feedback`)
    }
    this.firstLoadData = false

    // interval fetch messages
    this.interval = setInterval(async () => {
      if (!this.reloadCurrentRoom) {
        await this.fetchMessageGroups({ ...this.messageGroupParams, _limit: this.messageGroups.length })
        if (this.messageGroup.id) {
          await this.fetchMessages(this.messageGroup)
        }
      } else {
        // reload current result when filter
        await this.fetchMessageGroups(this.searchParams)
        if (this.messageGroup.id) {
          await this.fetchMessages(this.messageGroup)
        }
      }
    }, 60000)
  },

  computed: {
    ...mapState('TeacherFeedback', ['messages', 'messageGroup', 'groupNo']),
    ...mapGetters('TeacherFeedback', ['messageGroups']),
    ...mapGetters('auth', ['profile', 'role']),
    ...mapGetters('constant', ['messageMemberType'])
  },
  methods: {
    ...mapActions('TeacherFeedback', [
      'fetchMessageGroups',
      'fetchMessages',
      'createMessage',
      'uploadImage',
      'searchMessageGroups'
    ]),

    async onFilterChanged(params) {
      if (!params.class && !params.student) this.reloadCurrentRoom = false
      else this.reloadCurrentRoom = true

      params.type = 'teacher'
      params['_limit'] = -1
      this.searchParams = params

      await this.searchMessageGroups(params)
    },
    onChange(file) {
      if (file) {
        if (typeof this.$rules.imageChat(file) === 'string') {
          this.$alert.error(this.$rules.imageChat(file))
          return
        }
        this.send(file)
      }
    },
    async send(image) {
      if (this.sending || (!image && (!this.content || !textHelpers.removeSpaces(this.content)))) return

      const data = {
        time: moment().toISOString(),
        content: this.content,
        sender: this.profile.user,
        messageGroup: this.messageGroup.id
      }
      try {
        this.sending = true
        const createdMessage = await this.createMessage(data)

        if (image) {
          // image
          let formData = new FormData()
          formData.append('files', image)
          formData.append('refId', createdMessage.id)
          formData.append('ref', 'message')
          formData.append('field', 'image')

          this.uploadImage(formData)
        }

        if (!image) this.content = ''
      } catch (error) {
        this.$alert.error('Gửi thất bại, vui lòng thử lại')
      } finally {
        this.sending = false
      }
    },
    loadMore() {
      if (this.messageGroups.length < this.groupNo && !this.busy) {
        this.busy = true

        setTimeout(async () => {
          await this.fetchMessageGroups({ ...this.messageGroupParams, _limit: this.messageGroups.length + 10 })
          this.busy = false
        }, 1500)
      }
    },
    onResize() {
      this.windowHeight = window.innerHeight
    },
    async groupClicked(group) {
      this.messageLoading = true
      const messageGroup = { ...group, isUpdateLastSeen: true }
      await this.fetchMessages(messageGroup)
      this.messageLoading = false
      if (this.$route.params.id !== group.id) this.$router.push(`/teacher-feedback/${group.id}`)
    }
  },
  filters: {
    getDisplayTime(time) {
      if (moment(time).isBetween(moment().startOf('day'), moment().endOf('day'))) {
        // today
        return moment(time).format('HH:mm')
      } else if (moment(time).year === moment().year) {
        return moment(time).format('DD/MM HH:mm')
      } else {
        return moment(time).format('DD/MM/YYYY HH:mm')
      }
    },
    getTeacherNames(messageGroup) {
      if (messageGroup.class && messageGroup.class.headTeachers && messageGroup.class.headTeachers.length) {
        return messageGroup.name
      }

      return 'Chưa có GVCN'
    },
    getLastMessageContent(messageGroup) {
      if (messageGroup.lastMessage && messageGroup.lastMessage.image) return 'Đã gửi 1 ảnh'
      return messageGroup.lastMessage && messageGroup.lastMessage.content
    }
  },
  mounted() {
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    clearInterval(this.interval)
  }
}
</script>

<style scoped>
.feedback-container {
  height: 100%;
  overflow: hidden;
}

.feedback-header {
  border-bottom: 1px solid #eee;
}

.message-groups-list {
  overflow: auto;
  border-right: 1px solid #eee;
}

.message-group-card {
  transition: all 0.2s ease;
}

.message-group-card.on-hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.message-group-card.selected {
  background: #e3f2fd;
  border-color: #2196f3;
}

.message-preview {
  color: #666;
  font-size: 0.9em;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-window {
  background: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
}

.chat-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chat-header {
  padding: 16px;
  background: white;
  border-bottom: 1px solid #eee;
  border-radius: 8px;
}

.messages-container {
  overflow-y: auto;
  padding: 16px;
}

.message-wrapper {
  margin-bottom: 16px;
}

.message-bubble {
  max-width: 70%;
  padding: 8px 12px;
  border-radius: 12px;
  position: relative;
}

.message-bubble.received {
  background: white;
  margin-right: auto;
}

.message-bubble.sent {
  background: #2196f3;
  color: white;
  margin-left: auto;
}

.message-time {
  font-size: 0.75em;
  color: #999;
  margin-top: 4px;
}

.image-message img {
  max-width: 100%;
  border-radius: 8px;
  cursor: pointer;
}

.chat-input-area {
  padding: 16px;
  background: white;
  border-top: 1px solid #eee;
  border-radius: 8px;
}

.empty-state,
.no-feedback {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.view-profile-link {
  color: #2196f3;
  text-decoration: none;
}

.system-message {
  text-align: center;
  color: #666;
  margin: 16px 0;
}
</style>
